import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { Field } from './Field.tsx';

export type AuthMode = 'signin' | 'signup' | 'code' | 'forgot';

export interface SignUpInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface SignUpOutcome {
  error: string | null;
  /** True when Supabase created the account but the email must be confirmed before signing in. */
  needsConfirmation: boolean;
}

export interface SignInProps {
  title: string;
  subtitle?: string | undefined;
  logoSrc?: string | undefined;
  /** When sign-in cannot work on this build, say why instead of showing a form. */
  problem?: string | null | undefined;
  appleEnabled: boolean;
  initialMode?: AuthMode | undefined;
  onApple: () => Promise<string | null>;
  onPassword: (email: string, password: string) => Promise<string | null>;
  onSendCode: (email: string) => Promise<string | null>;
  onVerifyCode: (email: string, code: string) => Promise<string | null>;
  onForgotPassword: (email: string) => Promise<string | null>;
  onResendConfirmation: (email: string) => Promise<string | null>;
  /** Omit to hide account creation. */
  onSignUp?: ((input: SignUpInput) => Promise<SignUpOutcome>) | undefined;
  signUpLabel?: string | undefined;
  signInLabel?: string | undefined;
  askName?: boolean | undefined;
  footer?: ReactNode;
}

export const MIN_PASSWORD = 8;

export function friendlyAuthError(message: string): string {
  if (
    /did not match the expected pattern|unexpected token|load failed|failed to fetch|networkerror/i.test(
      message,
    )
  )
    return "Sign-in couldn't reach the login service. Check your connection, refresh the page, and try again.";
  if (/rate limit|too many/i.test(message))
    return 'Too many attempts. Wait a minute, then try again.';
  if (/invalid login credentials/i.test(message)) return "That email and password don't match.";
  if (/email not confirmed/i.test(message))
    return 'Confirm your email first. Use the link we sent, or send it again below.';
  if (/already registered|already exists/i.test(message))
    return 'An account with this email already exists. Sign in, or use Forgot password.';
  if (/provider is not enabled|unsupported provider/i.test(message))
    return "Sign in with Apple isn't switched on yet.";
  if (/signups not allowed|signup is disabled/i.test(message))
    return "There's no account with this email yet. Create one first.";
  if (/error sending|sending .*email|smtp|not authorized|unable to send/i.test(message))
    return "We couldn't send the email right now. Try again in a few minutes, or sign in with your password.";
  if (/token has expired|invalid.*otp|otp.*invalid|expired or is invalid/i.test(message))
    return 'That code did not match or has expired. Send a new one and use the newest email.';
  return message;
}

function AppleGlyph() {
  return (
    <svg width="16" height="19" viewBox="0 0 814 1000" aria-hidden="true" fill="currentColor">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

const appleButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  minHeight: 48,
  borderRadius: 8,
  border: 0,
  background: '#000',
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
};
const linkButton: CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  minHeight: 44,
  color: 'var(--ink-soft)',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 14,
  textDecoration: 'underline',
};
const tab = (active: boolean): CSSProperties => ({
  flex: 1,
  minHeight: 44,
  border: 0,
  borderBottom: `3px solid ${active ? 'var(--crave-red)' : 'var(--line)'}`,
  background: 'none',
  fontWeight: active ? 700 : 500,
  fontSize: 16,
  color: active ? 'var(--ink)' : 'var(--ink-soft)',
  cursor: 'pointer',
});

interface Notice {
  title: string;
  body: string;
  resend?: 'confirmation' | 'reset' | undefined;
}

/** One sign-in screen for every app: password, new account, emailed code, reset, and Apple. */
export function SignIn(p: SignInProps) {
  const [mode, setMode] = useState<AuthMode>(
    p.initialMode === 'signup' && !p.onSignUp ? 'signin' : (p.initialMode ?? 'signin'),
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const signUpLabel = p.signUpLabel ?? 'Create account';
  const signInLabel = p.signInLabel ?? 'Sign in';

  function go(next: AuthMode) {
    setMode(next);
    setError(null);
    setInfo(null);
    setUnconfirmed(false);
    setCode('');
    setCodeSent(false);
  }

  async function run(fn: () => Promise<string | null>, next?: () => void) {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const err = await fn();
      if (err) {
        setUnconfirmed(/email not confirmed/i.test(err));
        setError(friendlyAuthError(err));
      } else next?.();
    } catch (e) {
      setError(e instanceof Error ? friendlyAuthError(e.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  const passwordToggle = (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44, fontSize: 14 }}>
      <input
        type="checkbox"
        checked={showPassword}
        onChange={(e) => setShowPassword(e.target.checked)}
        style={{ width: 18, height: 18 }}
      />
      Show password
    </label>
  );

  const apple = p.appleEnabled && (mode === 'signin' || mode === 'signup') && (
    <>
      <button type="button" style={appleButton} disabled={busy} onClick={() => void run(p.onApple)}>
        <AppleGlyph /> Continue with Apple
      </button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: 'var(--ink-soft)',
          fontSize: 13,
        }}
      >
        <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        or
        <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>
    </>
  );

  let body: ReactNode;
  if (notice) {
    body = (
      <div style={{ display: 'grid', gap: 12 }}>
        <strong style={{ fontSize: 18 }}>{notice.title}</strong>
        <p style={{ margin: 0 }}>{notice.body}</p>
        {error && <p style={{ margin: 0, color: 'var(--crave-red)' }}>{error}</p>}
        {info && <p style={{ margin: 0, color: 'var(--ok)' }}>{info}</p>}
        {notice.resend && (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() =>
              void run(
                () =>
                  notice.resend === 'confirmation'
                    ? p.onResendConfirmation(email)
                    : p.onForgotPassword(email),
                () => setInfo('Sent again. Check your inbox and spam folder.'),
              )
            }
          >
            {busy ? 'Sending…' : 'Send it again'}
          </Button>
        )}
        <button
          type="button"
          style={linkButton}
          onClick={() => {
            setNotice(null);
            go('signin');
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  } else if (mode === 'signin') {
    body = (
      <form
        style={{ display: 'grid', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(() => p.onPassword(email, password));
        }}
      >
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          error={error ?? undefined}
        />
        {passwordToggle}
        {unconfirmed && (
          <button
            type="button"
            style={linkButton}
            disabled={busy}
            onClick={() =>
              void run(
                () => p.onResendConfirmation(email),
                () => setInfo('Confirmation email sent. Open the link, then sign in.'),
              )
            }
          >
            Send the confirmation email again
          </button>
        )}
        {info && <p style={{ margin: 0, color: 'var(--ok)' }}>{info}</p>}
        <Button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : signInLabel}
        </Button>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <button type="button" style={linkButton} onClick={() => go('forgot')}>
            Forgot password?
          </button>
          <button type="button" style={linkButton} onClick={() => go('code')}>
            Email me a sign-in code
          </button>
        </div>
      </form>
    );
  } else if (mode === 'signup' && p.onSignUp) {
    const signUp = p.onSignUp;
    body = (
      <form
        style={{ display: 'grid', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (password.length < MIN_PASSWORD) {
            setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
            return;
          }
          setBusy(true);
          setError(null);
          void signUp({ email, password, firstName, lastName })
            .then((r) => {
              if (r.error) setError(friendlyAuthError(r.error));
              else if (r.needsConfirmation)
                setNotice({
                  title: 'Check your email',
                  body: `We sent a confirmation link to ${email.trim()}. Open it on this device to finish creating your account. Check spam if you don't see it.`,
                  resend: 'confirmation',
                });
            })
            .catch((err: unknown) =>
              setError(
                err instanceof Error ? friendlyAuthError(err.message) : 'Something went wrong.',
              ),
            )
            .finally(() => setBusy(false));
        }}
      >
        {p.askName && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field
              label="First name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Field
              label="Last name"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        )}
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Create a password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={MIN_PASSWORD}
          hint={`At least ${MIN_PASSWORD} characters.`}
          error={error ?? undefined}
        />
        {passwordToggle}
        <Button type="submit" disabled={busy}>
          {busy ? 'Creating…' : signUpLabel}
        </Button>
        <button type="button" style={linkButton} onClick={() => go('signin')}>
          Already have an account? Sign in
        </button>
      </form>
    );
  } else if (mode === 'code') {
    body = !codeSent ? (
      <form
        style={{ display: 'grid', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(
            () => p.onSendCode(email),
            () => setCodeSent(true),
          );
        }}
      >
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          No password needed. We email you a one-time code.
        </p>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          error={error ?? undefined}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Email me a code'}
        </Button>
        <button type="button" style={linkButton} onClick={() => go('signin')}>
          Use a password instead
        </button>
      </form>
    ) : (
      <form
        style={{ display: 'grid', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(() => p.onVerifyCode(email, code));
        }}
      >
        <p style={{ margin: 0 }}>
          We sent a sign-in email to <strong>{email}</strong>. Enter the code from it, or tap its
          link on this device.
        </p>
        <Field
          label="Code from the email"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          required
          error={error ?? undefined}
        />
        {info && <p style={{ margin: 0, color: 'var(--ok)' }}>{info}</p>}
        <Button type="submit" disabled={busy || code.length < 6}>
          {busy ? 'Checking…' : 'Sign in'}
        </Button>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            style={linkButton}
            disabled={busy}
            onClick={() =>
              void run(
                () => p.onSendCode(email),
                () => setInfo('A new code is on its way.'),
              )
            }
          >
            Send a new code
          </button>
          <button type="button" style={linkButton} onClick={() => go('code')}>
            Use a different email
          </button>
        </div>
      </form>
    );
  } else {
    body = (
      <form
        style={{ display: 'grid', gap: 12 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(
            () => p.onForgotPassword(email),
            () =>
              setNotice({
                title: 'Check your email',
                body: `If there's an account for ${email.trim()}, we sent a link to set a new password. Open it on this device.`,
                resend: 'reset',
              }),
          );
        }}
      >
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Enter your email and we'll send a link to set a new password.
        </p>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          error={error ?? undefined}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Email me a reset link'}
        </Button>
        <button type="button" style={linkButton} onClick={() => go('signin')}>
          Back to sign in
        </button>
      </form>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        {p.logoSrc && (
          <img
            src={p.logoSrc}
            alt="Crave Pizza"
            style={{ height: 56, width: 'auto', justifySelf: 'start' }}
          />
        )}
        <h1 style={{ fontWeight: 800, margin: 0 }}>{p.title}</h1>
        {p.subtitle && <p style={{ margin: 0, color: 'var(--ink-soft)' }}>{p.subtitle}</p>}
      </div>
      {p.problem ? (
        <Card>
          <p style={{ margin: 0 }}>Sign-in isn't available on this build. {p.problem}</p>
        </Card>
      ) : (
        <Card>
          <div style={{ display: 'grid', gap: 14 }}>
            {p.onSignUp && !notice && (mode === 'signin' || mode === 'signup') && (
              <div role="tablist" style={{ display: 'flex' }}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'signin'}
                  style={tab(mode === 'signin')}
                  onClick={() => go('signin')}
                >
                  {signInLabel}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'signup'}
                  style={tab(mode === 'signup')}
                  onClick={() => go('signup')}
                >
                  {signUpLabel}
                </button>
              </div>
            )}
            {!notice && apple}
            {body}
          </div>
        </Card>
      )}
      {p.footer}
    </div>
  );
}
